import React, { useState, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { Note } from 'tonal';
import type { MelodicNote, VoicePart, NoteValue } from '@/types';
import { NOTE_VALUE_TO_BEATS } from '@/types/voice-line';
import { useVoiceLineStore } from '@/store/voice-line-store';
import { ContextMenu, type ContextMenuItem } from '@/components/UI/ContextMenu';
import styles from './NoteDot.module.css';

interface NoteDotProps {
  note: MelodicNote;
  x: number;
  y: number;
  color: string;
  isSelected: boolean;
  isPlaying: boolean;
  voicePart: VoicePart;
  laneHeight: number;
  laneWidth: number;
  beatWidth: number;
  zoom: number;
  snapResolution: number;
  /** Chord interval label (R, 3, 5, 7, etc.) - GRE-116 */
  intervalLabel?: string | null;
  onSelect: (id: string, multiSelect: boolean) => void;
  onDrag: (id: string, newY: number) => void;
  onDragEnd: (id: string, finalY: number, finalX?: number) => void;
  onConflictDragStart?: (noteId: string, note: MelodicNote, event: React.MouseEvent<HTMLDivElement>) => void;
  onHoldStart?: () => void;
  onHoldEnd?: () => void;
}

const LANE_PADDING = 8;

export const NoteDot: React.FC<NoteDotProps> = ({
  note,
  x,
  y,
  color,
  isSelected,
  isPlaying,
  voicePart,
  laneHeight,
  laneWidth,
  beatWidth,
  zoom,
  snapResolution,
  intervalLabel,
  onSelect,
  onDrag: _onDrag,
  onDragEnd,
  onConflictDragStart,
  onHoldStart,
  onHoldEnd,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingLyric, setIsEditingLyric] = useState(false);
  const [lyricInput, setLyricInput] = useState(note.text || '');
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // Track cumulative drag offset
  const justDraggedRef = useRef(false); // Prevent click after drag
  const lyricInputRef = useRef<HTMLInputElement>(null);

  // Calculate scaled beat width for snapping
  const scaledBeatWidth = beatWidth * zoom;

  // Get store actions for note manipulation
  const updateNote = useVoiceLineStore((state) => state.updateNote);
  const deleteNote = useVoiceLineStore((state) => state.deleteNote);

  // Context menu state
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingLyric && lyricInputRef.current) {
      lyricInputRef.current.focus();
      lyricInputRef.current.select();
    }
  }, [isEditingLyric]);

  // Sync lyricInput with note.text when it changes externally
  useEffect(() => {
    if (!isEditingLyric) {
      setLyricInput(note.text || '');
    }
  }, [note.text, isEditingLyric]);

  // Calculate drag constraints based on lane dimensions
  const constraints = useMemo(() => {
    const minY = LANE_PADDING; // Top of usable area
    const maxY = laneHeight - LANE_PADDING; // Bottom of usable area
    const minX = 0; // Left edge
    const maxX = laneWidth; // Right edge
    return { minY, maxY, minX, maxX };
  }, [laneHeight, laneWidth]);

  // Get pitch name from MIDI
  const pitchName = note.pitch || Note.fromMidi(note.midi) || 'C4';

  const handleDragStart = (event: MouseEvent | TouchEvent | PointerEvent, _info: PanInfo) => {
    // Stop propagation to prevent canvas drag
    // (PanInfo carries point/delta/offset/velocity, no .event field — the old
    // `info?.event?.stopPropagation` check was dead code against the real shape.)
    event?.stopPropagation?.();

    setIsDragging(true);
    dragOffsetRef.current = { x: 0, y: 0 };
    justDraggedRef.current = false;
    // Start hold tracking for smart snap
    onHoldStart?.();
    // Call conflict detection hook's drag start
    if (onConflictDragStart) {
      onConflictDragStart(note.id, note, event as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Track the offset but don't update store during drag
    dragOffsetRef.current = { x: info.offset.x, y: info.offset.y };
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    justDraggedRef.current = true;

    // Calculate final Y position (pitch)
    const finalY = Math.max(constraints.minY, Math.min(constraints.maxY, y + dragOffsetRef.current.y));

    // Calculate final X position with snap
    // Use the visual offset from drag to get the target beat
    const offsetBeats = dragOffsetRef.current.x / scaledBeatWidth;
    const targetBeat = note.startBeat + offsetBeats;

    // Snap the target beat to the current resolution
    const snappedBeat = Math.max(0, Math.round(targetBeat / snapResolution) * snapResolution);
    const finalX = snappedBeat * scaledBeatWidth;

    console.log('SNAP DEBUG:', {
      dragOffsetX: dragOffsetRef.current.x,
      scaledBeatWidth,
      offsetBeats,
      noteStartBeat: note.startBeat,
      targetBeat,
      snapResolution,
      snappedBeat,
      finalX,
    });

    // End hold tracking AFTER calculating snap (so we use the refined resolution)
    onHoldEnd?.();

    onDragEnd(note.id, finalY, finalX);
    dragOffsetRef.current = { x: 0, y: 0 };

    // Reset the flag after a short delay to allow click event to be ignored
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 100);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Ignore click if we just finished dragging
    if (justDraggedRef.current) return;

    const multiSelect = e.metaKey || e.ctrlKey || e.shiftKey;
    onSelect(note.id, multiSelect);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  };

  // Context menu items for note manipulation
  const contextMenuItems: ContextMenuItem[] = useMemo(() => {
    const currentAccidental = note.accidental;

    // Duration submenu items
    const durationOptions: NoteValue[] = ['whole', 'half', 'quarter', 'eighth', 'sixteenth', 'thirtysecond'];
    const durationLabels: Record<NoteValue, string> = {
      whole: 'Whole Note (4 beats)',
      half: 'Half Note (2 beats)',
      quarter: 'Quarter Note (1 beat)',
      eighth: 'Eighth Note (0.5 beats)',
      sixteenth: 'Sixteenth Note (0.25 beats)',
      thirtysecond: 'Thirty-second Note (0.125 beats)',
    };

    return [
      // Accidental options
      {
        id: 'accidental-natural',
        label: currentAccidental === null ? '✓ Natural' : 'Natural',
        action: () => {
          updateNote(voicePart, note.id, { accidental: null });
          setContextMenuOpen(false);
        },
      },
      {
        id: 'accidental-sharp',
        label: currentAccidental === 'sharp' ? '✓ Sharp (♯)' : 'Sharp (♯)',
        action: () => {
          updateNote(voicePart, note.id, { accidental: 'sharp' });
          setContextMenuOpen(false);
        },
      },
      {
        id: 'accidental-flat',
        label: currentAccidental === 'flat' ? '✓ Flat (♭)' : 'Flat (♭)',
        action: () => {
          updateNote(voicePart, note.id, { accidental: 'flat' });
          setContextMenuOpen(false);
        },
      },
      { id: 'divider-1', label: '', divider: true },
      // Duration submenu
      {
        id: 'duration',
        label: 'Duration',
        submenu: durationOptions.map((dur) => ({
          id: `duration-${dur}`,
          label: note.duration === NOTE_VALUE_TO_BEATS[dur] ? `✓ ${durationLabels[dur]}` : durationLabels[dur],
          action: () => {
            updateNote(voicePart, note.id, { duration: NOTE_VALUE_TO_BEATS[dur] });
            setContextMenuOpen(false);
          },
        })),
      },
      { id: 'divider-2', label: '', divider: true },
      // Rest toggle
      {
        id: 'toggle-rest',
        label: note.isRest ? 'Make Note' : 'Make Rest',
        action: () => {
          updateNote(voicePart, note.id, { isRest: !note.isRest });
          setContextMenuOpen(false);
        },
      },
      { id: 'divider-3', label: '', divider: true },
      // Delete
      {
        id: 'delete-note',
        label: 'Delete Note',
        action: () => {
          deleteNote(voicePart, note.id);
          setContextMenuOpen(false);
        },
      },
    ];
  }, [note.accidental, note.isRest, note.id, note.duration, voicePart, updateNote, deleteNote]);

  // Double-click to edit lyric inline
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingLyric(true);
  };

  // Save lyric on blur or Enter
  const handleLyricBlur = () => {
    setIsEditingLyric(false);
    if (lyricInput !== (note.text || '')) {
      updateNote(voicePart, note.id, { text: lyricInput || undefined });
    }
  };

  const handleLyricKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLyricBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setLyricInput(note.text || '');
      setIsEditingLyric(false);
    } else if (e.key === 'Tab') {
      // Allow Tab to move to next note's lyric (future enhancement)
      handleLyricBlur();
    }
  };

  // Determine visual state classes for wrapper
  const wrapperClasses = [
    styles.noteDotWrapper,
    isDragging && styles.dragging,
  ]
    .filter(Boolean)
    .join(' ');

  // NCT abbreviations for badge display
  const NCT_ABBREVIATIONS: Record<string, string> = {
    passing: 'p',
    neighbor: 'n',
    suspension: 's',
    anticipation: 'ant',
    escape: 'e',
    appoggiatura: 'app',
    pedal: 'ped',
    retardation: 'ret',
  };

  // Determine chord tone status - default to hollow (false) if not analyzed
  const isChordTone = note.analysis?.isChordTone ?? false;
  const nctType = note.analysis?.nonChordToneType;
  const nctAbbrev = nctType ? NCT_ABBREVIATIONS[nctType] : null;

  // Determine visual state classes for inner dot
  const dotClasses = [
    styles.noteDot,
    isSelected && styles.selected,
    isPlaying && styles.playing,
    note.accidental === 'sharp' && styles.sharp,
    note.accidental === 'flat' && styles.flat,
    isChordTone && styles.chordTone,
    !isChordTone && styles.nonChordTone,
  ]
    .filter(Boolean)
    .join(' ');

  // Use a key that includes midi AND startBeat to force remount after drag updates position
  // This resets Framer Motion's internal transform state
  const motionKey = `${note.id}-${note.midi}-${note.startBeat}`;

  return (
    <motion.div
      key={motionKey}
      className={wrapperClasses}
      data-no-pan="true"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        color: color, // Used by currentColor for border
      }}
      drag
      dragConstraints={{
        top: constraints.minY - y,
        bottom: constraints.maxY - y,
        left: constraints.minX - x,
        right: constraints.maxX - x,
      }}
      dragElastic={0}
      dragMomentum={false}
      onDragStart={(event, info) => {
        handleDragStart(event, info);
      }}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      role="button"
      tabIndex={0}
      aria-label={`Note ${pitchName} at beat ${note.startBeat}`}
    >
      <motion.div
        className={dotClasses}
        whileDrag={{ scale: 1.3 }}
        animate={{
          scale: isPlaying ? 1.2 : 1,
        }}
        transition={{
          type: 'spring',
          damping: 20,
          stiffness: 300,
        }}
      />
      <AnimatePresence>
        {isHovered && (
          <motion.div
            className={styles.tooltip}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {pitchName}
          </motion.div>
        )}
      </AnimatePresence>
      {/* NCT type badge */}
      {nctAbbrev && (
        <div className={styles.nctBadge}>{nctAbbrev}</div>
      )}
      {/* Chord interval label (GRE-116) */}
      {intervalLabel && !nctAbbrev && (
        <div className={styles.intervalBadge}>{intervalLabel}</div>
      )}
      {/* Lyric/syllable text - inline editable */}
      {isEditingLyric ? (
        <input
          ref={lyricInputRef}
          type="text"
          className={styles.lyricInput}
          value={lyricInput}
          onChange={(e) => setLyricInput(e.target.value)}
          onBlur={handleLyricBlur}
          onKeyDown={handleLyricKeyDown}
          onClick={(e) => e.stopPropagation()}
          placeholder="lyric"
        />
      ) : note.text ? (
        <div className={styles.lyricText}>
          {note.text}
        </div>
      ) : null}
      {/* Note context menu */}
      <ContextMenu
        isOpen={contextMenuOpen}
        position={contextMenuPosition}
        items={contextMenuItems}
        onClose={() => setContextMenuOpen(false)}
      />
    </motion.div>
  );
};
